'use client';

import { Dropdown, Image, TextLink } from '@petsmart-ui/sparky';
import classNames from 'classnames';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { dataLayerPush, getBaseUrlJoined, layoutEvents, trackCheckoutSignUpImpression } from '@libs/analytics';
import { getENV, IS_SSR, SignInCreateAccountButton, useAuthSignInClick, useOptimizely } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';
import TreatsIcon from './treats-rewards.icon.svg';
import './SignIn.scss';
export const trackingData = (data: object) => Object.assign(data, {
  ...layoutEvents.headerClick
});
function getSignInButtonText(isAuth0Enabled: boolean, isLoyaltyAccountMenu: boolean) {
  if (isAuth0Enabled) return 'Sign in / Create account';
  if (isLoyaltyAccountMenu) return 'Sign in';
  return 'Login';
}
export function SignIn() {
  const [isCartPage, setIsCartPage] = useState(false);
  const {
    context
  } = useOptimizely();
  const {
    isAuth0Enabled,
    handleSignInClick
  } = useAuthSignInClick();
  const currentPage = IS_SSR ? '' : new URL(window.location.href).pathname;
  useEffect(() => {
    setIsCartPage(['/checkout-login', '/cart'].includes(currentPage));
  }, [currentPage]);
  const redirectURL = isCartPage ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=${encodeURIComponent(currentPage)}` : NAVIGATION_URLS.velocity.createAccount;
  const isLoyaltyAccountMenu = getENV('NEXT_PUBLIC_LOYALTY_ACCOUNT_MENU');
  const wrapperClasses = useMemo(() => classNames('legacy-signin', isLoyaltyAccountMenu ? 'loyalty-account-menu-enabled' : ''), [isLoyaltyAccountMenu]);

  // For the main SignIn
  const handleSignInLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    dataLayerPush(trackingData({
      selection: 'login',
      link_url: getBaseUrlJoined(NAVIGATION_URLS.legacy.account.base)
    }));
    if (isAuth0Enabled) {
      handleSignInClick();
    } else {
      window.location.assign(NAVIGATION_URLS.velocity.signIn);
    }
  }, [handleSignInClick, isAuth0Enabled]);

  // For the SignIn button in the dropdown
  const handleSignInButtonClick = useCallback(() => {
    context?.trackEvent('sign_ins');
    trackCheckoutSignUpImpression();
    handleSignInClick();
  }, [handleSignInClick]);
  function handleTreatsEvent() {
    context?.trackEvent('treads_rewards_clicks');
  }
  const handleCreateAccountClick = () => {
    context?.trackEvent('create_account');
  };
  return <div className={wrapperClasses} data-testid="sign-in">
            <div className="legacy-signin__button">
                <div className="legacy-signin__copy">
                    <TextLink href={NAVIGATION_URLS.velocity.signIn} onClick={handleSignInLinkClick} className="legacy-signin__link">
                        <span>Sign In</span>
                        <span className="legacy-account__caret" />
                    </TextLink>
                </div>
            </div>

            <div className="legacy-signin__dropdown-wrapper">
                <Dropdown className="legacy-signin__dropdown">
                    <section className="signin-dropcontent">
                        <div className="signin-dropcontent__container">
                            {isLoyaltyAccountMenu ? <TreatsIcon className="sparky-c-logo--rewards" /> : <Image src="https://s7d2.scene7.com/is/image/PetSmart/LY_2_SignInAccountHover_treatslogo_allsize_0611?UT0501L$" alt="Loyalty Logo" className="signin-dropcontent__logo" loading="lazy" width={130} height={26} />}

                            <div className="signin-dropcontent__content">
                                <p>
                                    {`Join our`}
                                    <TextLink className="signin-dropcontent__loyalty" href="/treats-rewards" onClick={handleTreatsEvent}>
                                        loyalty program
                                    </TextLink>
                                    {`& earn points every time you shop!`}
                                </p>

                                <SignInCreateAccountButton className="signin-dropcontent__button" fullWidth onClick={handleSignInButtonClick} text={getSignInButtonText(isAuth0Enabled, isLoyaltyAccountMenu)} type="button" variant="primary" />

                                {!isAuth0Enabled && <TextLink className="signin-dropcontent__textlink" href={redirectURL} onClick={handleCreateAccountClick}>
                                        {isLoyaltyAccountMenu ? 'Create account' : 'Sign up'}
                                    </TextLink>}
                            </div>
                        </div>
                    </section>
                </Dropdown>
            </div>

            <div className="legacy-signin__accent" />
        </div>;
}